import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, skip, switchMap, take } from 'rxjs/operators';

import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { unauthorizedContentRedirectKey } from '@core/authentication/redirect-util';
import { PlatformService } from '@core/services/platform.service';
import { StorageService } from '@core/services/storage.service';
import { LoginPanelComponent } from '@features/login-panel/login-panel/login-panel.component';
import { OverlayService } from '@libs/overlay';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { isTokenExpired } from '@core/authentication/authentication.helper';
import { authExpiresAtKey } from '@core/authentication/authentication.models';
import { Router } from '@angular/router';

@Injectable()
export class LoginRequiredInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private platformService: PlatformService,
        private overlayService: OverlayService,
        private storageService: StorageService,
        private authenticationService: AuthenticationService,
        private siteStateService: SiteStateService
    ) {}

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.platformService.isBrowser) {
            return next.handle(request);
        }

        return next.handle(request).pipe(
            catchError(error => {
                //We get 401 if token has expired.
                if (error.status === 401) {
                    const contentRequest = this.getContentRequest(request.url);
                    if (contentRequest) {
                        // Store what page the user meant to go to
                        this.storageService.set(unauthorizedContentRedirectKey, contentRequest);
                    }

                    return this.handle401(request.url).pipe(
                        take(1),
                        switchMap(token => {
                            if (token) {
                                return next.handle(
                                    request.clone({
                                        setHeaders: { Authorization: `Bearer ${token}` }
                                    })
                                );
                            }

                            return EMPTY;
                        })
                    );
                }

                return throwError(error);
            })
        );
    }

    /**
     * This method returns an outer observable thats checks if the user is/was logged in
     * if not it triggers the login-panel. But if the user was logged in, it returns an
     * inner observable, skips the first emit, tries to log in based on success fail
     * it triggers the overlay or passes a new token
     * @param url
     */
    private handle401(url: string): Observable<string | undefined> {
        const isContentRequest = Boolean(this.getContentRequest(url));

        return this.authenticationService.loginState$.pipe(
            take(1),
            switchMap(({ loggedIn: wasLoggedIn, token: currentToken, authMode }) => {
                if (!wasLoggedIn) {
                    this.triggerOverlay(isContentRequest);
                    return EMPTY;
                }

                this.siteStateService.setState('progress', true);
                // If user was logged in we try to log the user in again silently
                // if this fails and it was a content request we show the login panel
                // any other request, fails silently
                const obs$ = this.authenticationService.loginState$.pipe(
                    skip(1),
                    take(1),
                    switchMap(({ loggedIn, token }) => {
                        this.siteStateService.setState('progress', false);
                        if (!loggedIn) {
                            // If the token could not be acquired silently,
                            // the refresh token expired and the login panel must be shown again
                            this.triggerOverlay(isContentRequest);
                            return EMPTY;
                        } else if (token !== currentToken) {
                            // If the token could be acquired silently,
                            // Remove content redirect key and pass the new token along
                            this.storageService.remove(unauthorizedContentRedirectKey);
                            return of(token);
                        }
                        return EMPTY;
                    })
                );

                const expiresAt: string | undefined = this.storageService.get(authExpiresAtKey);
                if (authMode === 'Vipps' && isTokenExpired(expiresAt)) {
                    this.router.navigate(['/']).then(() => {
                        this.authenticationService.clearActiveToken();
                        this.authenticationService.resetLoginState({
                            loggedIn: false,
                            error: 'expired_token',
                            authMode
                        });
                    });
                } else {
                    this.authenticationService.login(authMode, undefined, true);
                }

                return obs$;
            })
        );
    }

    /**
     * Only trigger login panel on content requests
     * @param isContentRequest
     */
    private triggerOverlay(isContentRequest: boolean) {
        if (!isContentRequest) {
            return;
        }

        this.overlayService
            .open(LoginPanelComponent)
            .afterClose()
            .pipe(
                take(1),
                switchMap(() => this.authenticationService.loginState$.pipe(map(({ loggedIn }) => loggedIn)))
            )
            .subscribe(loggedIn => {
                // if not logged remove the page the user meant to go to
                if (!loggedIn) {
                    this.storageService.remove(unauthorizedContentRedirectKey);
                }
            });
    }

    private getContentRequest(url: string): string | undefined {
        const contentMatch = url.match('content/page/([^?^#]*)');
        if (contentMatch && contentMatch.length > 1) {
            return contentMatch[1];
        }
        return undefined;
    }
}
